import { twMerge } from 'tailwind-merge';

import { FontWeight, Heading, LiliAutoComplete } from '@lili/lili-frontend-common';
import { removeAllSpaces } from '@lili/utils/formatting';
import type { LiliDropdownItem } from '@lili/lili-frontend-common';

interface CustomRadioButtonProps {
  checked: boolean;
  onChange: (val: string) => void;
  name: string;
  value: string;
  label: string;
  inputId?: string;
}

export const CustomRadioButton = ({
  checked,
  onChange,
  label,
  name,
  value,
  inputId,
}: CustomRadioButtonProps) => {
  return (
    <label className='flex items-center cursor-pointer gap-[8px]'>
      <div className='relative'>
        {/* Hidden native radio button for accessibility */}
        <input
          type='radio'
          className='sr-only'
          checked={checked}
          onChange={() => onChange(value)}
          name={name}
          value={value}
        />

        {/* Custom radio button appearance */}
        <div
          id={removeAllSpaces(inputId || '')}
          onClick={() => onChange(value)}
          className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center hover:bg-surface-primary-bw-00 hover:border-surface-brand-action-03 ${checked ? 'border-surface-brand-action-03 bg-surface-primary-bw-00' : 'border-outline-primary-bw-06'}`}>
          {checked && <div className='w-3 h-3 rounded-full bg-surface-brand-action-03'></div>}
        </div>
      </div>

      {/* Label text if provided */}
      {label && (
        <Heading.H10 className='text-text-main-primary' fontWeight={FontWeight.REGULAR}>
          {label}
        </Heading.H10>
      )}
    </label>
  );
};

interface ISelectionGroup {
  selectedCountry: LiliDropdownItem | undefined;
  countryDataList: { title: string; value: string }[];
  groupTitle: string;
  groupValue: string;
  optionsList: {
    groupId: string;
    value: string;
    label: string;
    checked: boolean;
    isExtendable: boolean;
    inputId?: string;
  }[];
  onGroupValueChange: (value: string) => void;
}

export const SelectionGroup = ({
  selectedCountry,
  countryDataList,
  groupTitle,
  optionsList,
  onGroupValueChange,
}: ISelectionGroup) => {
  return (
    <div className='flex flex-col gap-[20px]'>
      <Heading.H8>{groupTitle}</Heading.H8>

      {optionsList.map((option) => {
        const wrapperStyles = twMerge(
          'h-0 max-h-0 overflow-hidden w-full',
          !option.checked && 'animate-expandElementReverse',
          option.checked && 'animate-expandElement'
        );

        return option.isExtendable ? (
          <div className='flex flex-col items-baseline gap-4'>
            <CustomRadioButton
              key={option.groupId}
              inputId={option.inputId || option.value}
              name={option.groupId}
              checked={option.checked}
              value={option.value}
              onChange={() => onGroupValueChange(option.value)}
              label={option.label}
            />
            <div className={wrapperStyles}>
              {option.checked && (
                <LiliAutoComplete
                  data-testid={`autoComplete_${option.inputId || option.value}`}
                  placeholder='Select Country'
                  onItemSelection={(item) => onGroupValueChange(item.value)}
                  selectedItem={
                    selectedCountry
                      ? { title: selectedCountry.title, value: selectedCountry.value }
                      : { title: '', value: undefined }
                  }
                  autoCompleteItems={countryDataList}
                />
              )}
            </div>
          </div>
        ) : (
          <CustomRadioButton
            inputId={option.inputId || option.value}
            key={option.groupId}
            name={option.groupId}
            checked={option.checked}
            value={option.value}
            onChange={() => onGroupValueChange(option.value)}
            label={option.label}
          />
        );
      })}
    </div>
  );
};
